import { Button, Loader, Popover, UnstyledButton } from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Suspense, lazy, useRef, useState } from "react";
import { ImageCropModal } from "@/components/ImageCropModal";
import {
  BRAND_EMOJIS,
  brandName,
  brandUrl,
  isUploadedIcon,
} from "@/lib/brandEmojis";
import { uploadLinkIcon } from "@/api/tenant";
import { notify } from "@/lib/notify";

// Seçici ~1 MB. Ana pakete girerse panelin AÇILIŞI yavaşlar, oysa emoji yalnızca şube düzenleme
// formunda gerekiyor — lazy import ilk tıklamaya kadar hiç indirmiyor.
const Picker = lazy(() => import("emoji-picker-react"));

// emojiStyle="native": kütüphanenin varsayılanı emoji görsellerini jsDelivr CDN'inden çekiyor.
// Sistem emojisi ile hem dış istek olmuyor hem de panelde görünen ile menüde görünen aynı oluyor
// (menü zaten emojiyi metin olarak basıyor). Değer EmojiStyle.NATIVE ile birebir aynı; enum'u
// import etmek modülü lazy sınırının dışına çıkarırdı.
const EMOJI_STYLE = "native";

// Menüde simge 20px'lik bir kutuda duruyor; 128 retina ve 2x yakınlaştırma için fazlasıyla yetiyor.
// PNG çünkü şeffaflık şart: simge menünün kendi zemini üzerine basılıyor.
const ICON_TARGET = { aspect: { width: 128, height: 128 }, type: "image/png" };
const MAX_ICON_SOURCE = 4 * 1024 * 1024;

// Bağlantı anahtarı seçtiren düğme. Üç biçim üretebiliyor ve üçü de aynı alanda (Branch.links[].icon)
// düz string olarak yaşıyor:
//
//   emoji            — picker'dan seçilen karakter
//   brand:<ad>       — Instagram/WhatsApp/Gmail gibi, Unicode'da karşılığı olmayan marka simgeleri
//   https://…        — kullanıcının kendi yüklediği simge (R2 adresi)
export function EmojiPicker({ value, onChange, disabled }) {
  const [opened, setOpened] = useState(false);
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);
  const brand = brandName(value);

  const upload = useMutation({
    mutationFn: uploadLinkIcon,
    onSuccess: (url) => {
      onChange(url);
      setFile(null);
      setOpened(false);
    },
    onError: () => notify.error("Simge yüklenemedi"),
  });

  const pickFile = (event) => {
    const chosen = event.target.files?.[0];

    // Aynı dosya ikinci kez seçilebilsin: input değeri değişmezse change olayı hiç doğmuyor.
    event.target.value = "";

    if (!chosen) {
      return;
    }

    if (chosen.size > MAX_ICON_SOURCE) {
      notify.warning("Dosya boyutu 4 MB'dan büyük olamaz");

      return;
    }

    setFile(chosen);
  };

  // Kadraj penceresi popover'ın DIŞINDA: popover kapanınca (kırpma penceresine tıklamak dışarı
  // tıklamak sayılıyor) içindeki her şey sökülürdü.
  if (file) {
    return (
      <ImageCropModal
        file={file}
        title="Bağlantı Simgesi"
        description="Menüde 20px'lik bir kutuda görünür. Zemini şeffaf bırak."
        aspect={ICON_TARGET.aspect}
        type={ICON_TARGET.type}
        confirming={upload.isPending}
        onConfirm={(cropped) => upload.mutateAsync(cropped)}
        onClose={() => setFile(null)}
      />
    );
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withArrow
      shadow="md"
      trapFocus
    >
      <Popover.Target>
        <UnstyledButton
          disabled={disabled}
          onClick={() => setOpened((current) => !current)}
          aria-label="Simge seç"
          style={{
            width: 42,
            height: 36,
            fontSize: 20,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-sm)",
            background: "var(--mantine-color-body)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {brand || isUploadedIcon(value) ? (
            <img
              src={brand ? brandUrl(brand) : value}
              alt=""
              width={20}
              height={20}
            />
          ) : (
            value
          )}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Suspense fallback={<Loader size="sm" m="md" />}>
          <Picker
            emojiStyle={EMOJI_STYLE}
            // Marka simgeleri: Unicode'da Instagram/WhatsApp/Gmail logosu yok, o yüzden
            // picker'a kendi kategorisi olarak ekleniyorlar (bkz. lib/brandEmojis).
            customEmojis={BRAND_EMOJIS}
            // Önizleme şeridi ve cilt tonu seçici yerin yarısını yiyor; burada tek iş
            // bir simge seçmek.
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            width={320}
            height={380}
            searchPlaceholder="Simge ara"
            // Marka simgesinde `emoji` alanı karakter değil id'yi taşıyor (kütüphane
            // custom emoji için emoji === unified === id veriyor); `isCustom` ile
            // açıkça ayırmak, o davranışa bel bağlamaktan güvenli.
            onEmojiClick={(emoji) => {
              onChange(emoji.isCustom ? emoji.unified : emoji.emoji);
              setOpened(false);
            }}
          />
        </Suspense>
        {/* Hazır setin dışına çıkmak isteyen için: emoji ve marka listesi sabit, kiracının
                    kendi rozeti (Yemeksepeti, Getir, kendi kampanya işareti) ancak böyle girebilir. */}
        <Button
          fullWidth
          variant="subtle"
          size="xs"
          leftSection={<IconUpload size={14} />}
          onClick={() => inputRef.current?.click()}
        >
          Kendi simgeni yükle
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={pickFile}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
